import { useDeferredValue, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useUniverseSecurityItems } from '@biasafe.queries/securities-universes-security-items/securities-universes-security-items';
import type { Security } from '@biasafe.models';
import type { Filters } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/config/constants/liquidityFilterData';
import {
  selectDataProcessor,
  useSmartBetaStrategyStore,
} from '@/features/Strategies/stores/definitionFormStore';
import { isActive } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/config/constants/liquidityFilterData';
import { superSectorForCode } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/config/constants/staticClassifications';
import { toDataStatus } from './dataStatus';
import { universeSecuritiesQuery } from '@/shared/config/queryDefaults';
import { useUniverseAsOfDate } from '../Steps/RebalancingSchedule/hooks/useLatestReconstitutionDate';

/** Maps UI size segment IDs to API SecuritySize values */
const SIZE_UI_TO_API: Record<string, string> = {
  large: 'LRG',
  mid: 'MID',
  small: 'SML',
};

interface UserFilterLookups {
  excludedCountrySet: Set<string>;
  excludedExchangeSet: Set<string>;
  blockedSet: Set<string>;
  sectorSet: Set<string>;
  industrySet: Set<string>;
  subIndustrySet: Set<string>;
  priceActive: boolean;
  advActive: boolean;
  floatActive: boolean;
  mcapActive: boolean;
  historyActive: boolean;
}

/**
 * 3-layer filtering engine:
 *
 * Layer 1 — Universe Securities: stocks belonging to the selected universe (hard scope)
 * Layer 2 — Scoped Securities: Layer 1 filtered by geographic scope + size segment
 * Layer 3 — Filtered Securities: Layer 2 filtered by user exclusions (type, GICS, country, liquidity)
 *
 * The UI sections show items from Layer 1 (what the universe contains).
 * Excluded counts reflect only Layer 2 → Layer 3 differences (user-initiated).
 */
// Stable identity so the shallow store selector doesn't see a new array on
// every render when the field is absent.
const EMPTY_EXCHANGES: Array<string> = [];

export function useFilteredSecurities() {
  // StructuralRules.primary_share_class_only — read from data_processor, the
  // SAME field the submission adapter serializes, so the preview count and the
  // backtest agree. (It used to read a separate StartingUniverse-store flag the
  // toggle wrote to, which drove the preview but never the submitted config.)
  const primaryShareClassOnly = useSmartBetaStrategyStore(
    (st) => st.data_processor.primary_share,
  );

  // Step 2's latest reconstitution date. The universe is resolved AT that
  // date so the eligible set and the portfolio it will rebalance are counted
  // at the same instant; it is also part of the queryKey, so changing the
  // schedule refetches instead of serving the previous date from cache.
  const asOf = useUniverseAsOfDate();
  const {
    data: rawSecurities = [],
    isLoading: isSecuritiesLoading,
    error,
  } = useUniverseSecurityItems(asOf ? { as_of: asOf } : undefined, {
    query: {
      ...universeSecuritiesQuery(asOf),
    },
  });

  const isLoading = isSecuritiesLoading;

  const {
    portfolioId,
    geographicScope,
    sizeSegment,
    cyclicality,
    excludeCountries,
    excludeExchanges,
    excludeType,
    excludeGicsSector,
    excludeGicsIndustries,
    excludeGicsSubIndustries,
    liquidity,
    size,
    history,
    singleStockRestriction,
  } = useSmartBetaStrategyStore(
    useShallow((state) => {
      const dp = selectDataProcessor(state);
      return {
        portfolioId: dp.universe.portfolio_id,
        geographicScope: dp.universe.geographic_scope,
        sizeSegment: dp.universe.size_segment,
        cyclicality: dp.universe.cyclicality,
        excludeCountries: dp.exclude_countries,
        // Optional: exclude_exchanges is a newer field, so state persisted
        // before it existed has no value here.
        excludeExchanges: dp.exclude_exchanges ?? EMPTY_EXCHANGES,
        excludeType: dp.exclude_type,
        excludeGicsSector: dp.exclude_gics_sector,
        excludeGicsIndustries: dp.exclude_gics_industries,
        excludeGicsSubIndustries: dp.exclude_gics_sub_industries,
        liquidity: dp.liquidity,
        size: dp.size,
        history: dp.history,
        singleStockRestriction: dp.single_stock_restriction,
      };
    }),
  );

  // =========================================================================
  // Layer 1: Universe Securities — stocks belonging to the selected universe
  // =========================================================================
  const universeSecurities = useMemo(() => {
    // `Security.portfolioIds` holds the SAME Morningstar portfolio ids as the
    // catalogue, so Layer 1 needs no catalogue lookup at all — one less thing
    // that can go wrong when ClickHouse is degraded. The previous lookup
    // matched on the display NAME and fell through to `return rawSecurities`
    // whenever it missed, which silently widened the universe to every
    // security: US Target Market Exposure (467 names, 1 country) reported
    // 2,220 constituents across 2 countries — all of North America.
    // An empty id (half-built form) means "no universe picked yet".
    if (!portfolioId) return rawSecurities;
    return rawSecurities.filter((s: Security) =>
      s.portfolioIds.includes(portfolioId),
    );
  }, [rawSecurities, portfolioId]);

  // Layer-1 total market cap (every universe constituent, before any wizard
  // filter). The coverage stat scales the index's published figure by the share
  // of this that survives the user's scope/size/exclusions.
  const universeMarketCap = useMemo(
    () => universeSecurities.reduce((sum, s) => sum + (s.marketCap ?? 0), 0),
    [universeSecurities],
  );

  // What exists in the universe — used to scope classification UI
  const universeScope = useMemo(() => {
    const securityTypes = new Set<string>();
    const sectorCodes = new Set<string>();
    const industryCodes = new Set<string>();
    const subIndustryCodes = new Set<string>();
    const countries = new Set<string>();
    const mics = new Set<string>();

    for (const s of universeSecurities) {
      securityTypes.add(s.securityType);
      sectorCodes.add(s.sectorCode);
      industryCodes.add(s.industryCode);
      subIndustryCodes.add(s.subIndustryCode);
      countries.add(s.country);
      if (s.mic) mics.add(s.mic);
    }

    return {
      securityTypes,
      sectorCodes,
      industryCodes,
      subIndustryCodes,
      countries,
      mics,
    };
  }, [universeSecurities]);

  // =========================================================================
  // Layer 2: Scoped Securities — filtered by scope + size + cyclicality
  // (universe-level inclusions)
  // =========================================================================
  const scopedSecurities = useMemo(() => {
    const scopeSet = new Set(geographicScope);
    const sizeSet = new Set(
      sizeSegment.map((s) => SIZE_UI_TO_API[s]).filter(Boolean),
    );
    // Empty scope/size means "no restriction", not "exclude everything".
    // Either round-tripping a saved strategy or selecting an unknown
    // universe id can leave these empty, which used to silently hide
    // every constituent.
    const anyScope = scopeSet.size === 0;
    const allSizesSelected =
      sizeSegment.length >= Object.keys(SIZE_UI_TO_API).length ||
      sizeSet.size === 0;
    // Cyclicality (super-sector) is a universe inclusion like scope/size,
    // stored as the list of KEPT super-sectors. Empty or all-three ⇒ no
    // restriction; otherwise keep only securities whose sector's super-sector
    // is selected. Without this, deselecting a super-sector in the Cyclicality
    // control had NO effect on the constituent count (the tech/90 vs 352 bug):
    // the count only ever reflected explicit per-sector GICS exclusions.
    const cyclicalitySet = new Set(cyclicality ?? []);
    const allCyclicality =
      cyclicalitySet.size === 0 || cyclicalitySet.size >= 3;

    return universeSecurities.filter((s) => {
      if (!anyScope && !scopeSet.has(s.scope)) return false;
      if (!allSizesSelected && (s.size === null || !sizeSet.has(s.size)))
        return false;
      if (
        !allCyclicality &&
        (!s.sectorCode || !cyclicalitySet.has(superSectorForCode(s.sectorCode)))
      )
        return false;
      return true;
    });
  }, [universeSecurities, geographicScope, sizeSegment, cyclicality]);

  // Per-selector drop counts for the universe-level INCLUSION selectors
  // (Geographic Scope / Size Segment / Cyclicality) — the "N excluded" badges
  // on their Step-1 headers. Counted over Layer 1 with each rule evaluated
  // INDEPENDENTLY (a security failing two selectors counts in both), mirroring
  // the Layer-3 per-filter counts, so one badge doesn't shift when an
  // unrelated selector changes. Same empty-means-everything semantics as
  // scopedSecurities above.
  const selectorExcludedCounts = useMemo(() => {
    const scopeSet = new Set(geographicScope);
    const sizeSet = new Set(
      sizeSegment.map((s) => SIZE_UI_TO_API[s]).filter(Boolean),
    );
    const anyScope = scopeSet.size === 0;
    const allSizesSelected =
      sizeSegment.length >= Object.keys(SIZE_UI_TO_API).length ||
      sizeSet.size === 0;
    const cyclicalitySet = new Set(cyclicality ?? []);
    const allCyclicality =
      cyclicalitySet.size === 0 || cyclicalitySet.size >= 3;
    let scopeExcluded = 0;
    let sizeSegmentExcluded = 0;
    let cyclicalityExcluded = 0;
    for (const s of universeSecurities) {
      if (!anyScope && !scopeSet.has(s.scope)) scopeExcluded++;
      if (!allSizesSelected && (s.size === null || !sizeSet.has(s.size)))
        sizeSegmentExcluded++;
      if (
        !allCyclicality &&
        (!s.sectorCode || !cyclicalitySet.has(superSectorForCode(s.sectorCode)))
      )
        cyclicalityExcluded++;
    }
    return { scopeExcluded, sizeSegmentExcluded, cyclicalityExcluded };
  }, [universeSecurities, geographicScope, sizeSegment, cyclicality]);

  // Data ranges from Layer 2 (scoped population, before user filters)
  const dataRanges = useMemo((): Filters => {
    if (scopedSecurities.length === 0) {
      return {
        price: [0, 0],
        adv: [0, 0],
        float: [0, 0],
        marketCap: [0, 0],
        history: [0, 0],
      };
    }
    let priceMin = Infinity,
      priceMax = -Infinity;
    let advMin = Infinity,
      advMax = -Infinity;
    let floatMin = Infinity,
      floatMax = -Infinity;
    let mcapMin = Infinity,
      mcapMax = -Infinity;
    let histMin = Infinity,
      histMax = -Infinity;
    for (const s of scopedSecurities) {
      if (s.price != null) {
        priceMin = Math.min(priceMin, s.price);
        priceMax = Math.max(priceMax, s.price);
      }
      // Null-guarded like `price` above: an unmeasured security must not drag
      // the slider's range down to 0 and make every measured name look liquid
      // by comparison.
      if (s.averageDollarVolume != null) {
        advMin = Math.min(advMin, s.averageDollarVolume);
        advMax = Math.max(advMax, s.averageDollarVolume);
      }
      const fp = s.floatFactor * 100;
      floatMin = Math.min(floatMin, fp);
      floatMax = Math.max(floatMax, fp);
      mcapMin = Math.min(mcapMin, s.marketCap);
      mcapMax = Math.max(mcapMax, s.marketCap);
      if (s.listingHistory != null) {
        histMin = Math.min(histMin, s.listingHistory);
        histMax = Math.max(histMax, s.listingHistory);
      }
    }
    return {
      price: [
        priceMin === Infinity ? 0 : priceMin,
        priceMax === -Infinity ? 0 : priceMax,
      ],
      adv: [
        advMin === Infinity ? 0 : advMin,
        advMax === -Infinity ? 0 : advMax,
      ],
      float: [floatMin, floatMax],
      marketCap: [mcapMin, mcapMax],
      history: [
        histMin === Infinity ? 0 : histMin,
        histMax === -Infinity ? 0 : histMax,
      ],
    };
  }, [scopedSecurities]);

  // =========================================================================
  // Layer 3: Filtered Securities — user exclusions applied to Layer 2
  // =========================================================================

  // User-filter lookups (scope/size already applied in Layer 2)
  const userFilterLookups = useMemo((): UserFilterLookups => {
    const excludedCountrySet = new Set(excludeCountries);
    // MIC codes, matching STATIC_EXCHANGES and the `mic` column.
    const excludedExchangeSet = new Set(excludeExchanges);
    const blockedSet = new Set(singleStockRestriction.blocked);
    const sectorSet = new Set(excludeGicsSector);
    const industrySet = new Set(excludeGicsIndustries);
    const subIndustrySet = new Set(excludeGicsSubIndustries);

    const priceActive = isActive(
      [liquidity.price_range.min, liquidity.price_range.max],
      dataRanges.price,
    );
    const advActive = isActive(
      [liquidity.avg_dollar_volume.min, liquidity.avg_dollar_volume.max],
      dataRanges.adv,
    );
    const floatActive = isActive(
      [liquidity.float.min, liquidity.float.max],
      dataRanges.float,
    );
    const mcapActive = isActive([size.min, size.max], dataRanges.marketCap);
    const historyActive = isActive(
      [history.min, history.max],
      dataRanges.history,
    );

    return {
      excludedCountrySet,
      excludedExchangeSet,
      blockedSet,
      sectorSet,
      industrySet,
      subIndustrySet,
      priceActive,
      advActive,
      floatActive,
      mcapActive,
      historyActive,
    };
  }, [
    excludeCountries,
    excludeExchanges,
    singleStockRestriction,
    excludeGicsSector,
    excludeGicsIndustries,
    excludeGicsSubIndustries,
    liquidity,
    size,
    history,
    dataRanges,
  ]);

  const deferredLookups = useDeferredValue(userFilterLookups);
  const deferredExcludeType = useDeferredValue(excludeType);

  // Single-pass bitmask over scoped securities (Layer 2).
  // Scope/size are NOT checked here — already applied in Layer 2.
  const filterResults = useMemo(() => {
    const filtered: Array<Security> = [];
    const availableSizes = new Set<string>();
    const availableTypes = new Set<string>();
    const availableCountryCodes = new Set<string>();
    const availableScopes = new Set<string>();
    const availableSectors = new Set<string>();
    const availableIndustries = new Set<string>();
    const availableSubIndustries = new Set<string>();
    // The five individual sliders…
    let priceExcluded = 0;
    let advExcluded = 0;
    let floatExcluded = 0;
    let marketCapExcluded = 0;
    let historyExcluded = 0;
    // …and the sections that group them.
    let liquidityExcluded = 0;
    let sizeExcluded = 0;
    // Group summaries. A parent card's badge is the UNION of the stocks its
    // own sub-filters drop — counted once per security, not summed across
    // sub-filters and never a count of excluded *codes*. Summing double-counts
    // every name that fails more than one sub-filter, which is the common case
    // (a REIT excluded both by type and by its GICS sector).
    let exchangeExcluded = 0;
    let classificationGroupExcluded = 0;
    let liquidityGroupExcluded = 0;
    // Rows the Primary-Share-Class rule drops, mirroring the engine's
    // `_apply_primary_share_class_filter`: keep exactly one row per issuer.
    // Counted here rather than in a separate pass so the toggle's own badge and
    // the group summary above it can never disagree — the group has to contain
    // it, since the toggle lives inside that card.
    let primaryShareClassExcluded = 0;
    const seenIssuers = new Set<string>();
    // Per-SUBSECTION counts, one level below the group summaries above: these
    // feed the "N excluded" badge on the sector header and on the geographic
    // header. Kept separate rather than derived from
    // `classificationGroupExcluded`, which is the union across type + sectors +
    // countries and so cannot answer "what does THIS section cost me".
    //
    // The three-level sector test mirrors the backtest engine exactly
    // (quant_toolbox/universe/sql_queries.py): it emits `sector_id NOT IN …`,
    // `industry_group_id NOT IN …` and `industry_id NOT IN …` as three
    // INDEPENDENT conditions, so a name drops if ANY of its levels is excluded.
    let gicsExcluded = 0;
    /** Securities the Security-Types filter drops — its own header badge. */
    let typeExcluded = 0;
    /** Country sub-count of the geographic filter. */
    let countryExcluded = 0;
    /**
     * What the geographic filter's badge reads: the union of its country AND
     * exchange sub-filters. `countryExcluded` and `exchangeExcluded` remain the
     * individual sub-counts.
     */
    let geographicExcluded = 0;

    for (const s of scopedSecurities) {
      // Structural rule first — the engine applies it at prefetch step 2,
      // ahead of every eligibility filter. Rows with no issuer id are always
      // kept and never count.
      let isDuplicateShareClass = false;
      if (primaryShareClassOnly && s.companyId) {
        if (seenIssuers.has(s.companyId)) {
          isDuplicateShareClass = true;
          primaryShareClassExcluded++;
        } else {
          seenIssuers.add(s.companyId);
        }
      }

      // The blocked list short-circuits every filter. There is no allow-list
      // counterpart: `ManualOverrides` carries `single_exclusions` only, so an
      // override that let a name bypass scope/size/cyclicality would show in
      // the preview count and never happen in the backtest. Matched on the
      // engine's performance_id (what single_exclusions stores), not the ticker.
      if (deferredLookups.blockedSet.has(s.performanceId ?? s.ticker)) continue;

      // Evaluate user-applied filter conditions (no scope/size — already in Layer 2)
      const passCountry = !deferredLookups.excludedCountrySet.has(s.country);
      if (!passCountry) countryExcluded++;
      // Exchange exclusions belong to the same filter as countries ("Regions,
      // Countries, & Exchanges") and are matched on the listing's MIC. A row
      // with no MIC can't be judged by a venue filter, so it passes.
      const passExchange =
        !s.mic || !deferredLookups.excludedExchangeSet.has(s.mic);
      // The badge on that filter's header covers the WHOLE filter, so it reads
      // this union rather than `countryExcluded` alone: countries and exchanges
      // share one header, one Reset and one advanced panel, and `clearGeo`
      // clears both. Counted once per security, per the group-summary rule
      // above — a name dropped by its country AND its venue is one name.
      // Without the union, an exchange-only exclusion showed NO badge at all
      // while it was removing constituents.
      if (!passCountry || !passExchange) geographicExcluded++;
      const passType =
        !deferredExcludeType[
          s.securityType as keyof typeof deferredExcludeType
        ];
      if (!passType) typeExcluded++;
      const passGics =
        !deferredLookups.sectorSet.has(s.sectorCode) &&
        !deferredLookups.industrySet.has(s.industryCode) &&
        !deferredLookups.subIndustrySet.has(s.subIndustryCode);
      if (!passGics) gicsExcluded++;

      // Each slider is evaluated independently, NOT short-circuited into one
      // `passLiquidity`. A name failing on price would otherwise leave every
      // later slider looking innocent, and each slider's own badge has to
      // report what IT removes.
      let passPrice = true;
      if (deferredLookups.priceActive && s.price != null) {
        passPrice =
          s.price >= liquidity.price_range.min &&
          s.price <= liquidity.price_range.max;
      }
      let passAdv = true;
      // Like price: a name with no ADV reading isn't excluded BY the ADV
      // slider — absent data is not a failed test.
      if (deferredLookups.advActive && s.averageDollarVolume != null) {
        passAdv =
          s.averageDollarVolume >= liquidity.avg_dollar_volume.min &&
          s.averageDollarVolume <= liquidity.avg_dollar_volume.max;
      }
      let passFloat = true;
      if (deferredLookups.floatActive) {
        const floatPct = s.floatFactor * 100;
        passFloat =
          floatPct >= liquidity.float.min && floatPct <= liquidity.float.max;
      }
      let passMcap = true;
      if (deferredLookups.mcapActive) {
        passMcap = s.marketCap >= size.min && s.marketCap <= size.max;
      }
      let passHistory = true;
      // `!= null` like ADV above: null means the security has no price history
      // on record, so a minimum-listing-history screen cannot judge it and must
      // not exclude it. ClickHouse only carries OHLCV for USD and CAD listings.
      // The pre-existing `isNaN(days) → exclude` below is deliberately the
      // opposite policy, for genuinely malformed values — those ARE measured.
      if (deferredLookups.historyActive && s.listingHistory != null) {
        const days =
          typeof s.listingHistory === 'number'
            ? s.listingHistory
            : Number(s.listingHistory);
        passHistory =
          !isNaN(days) && days >= history.min && days <= history.max;
      }
      const passLiquidity =
        passPrice && passAdv && passFloat && passMcap && passHistory;

      const passesAll =
        passCountry && passExchange && passType && passGics && passLiquidity;

      if (passesAll) filtered.push(s);

      // Union counts for the two parent cards. Deliberately independent of
      // each other: a name dropped by BOTH its sector and its market cap
      // counts once on each card, because each card answers "how many names
      // do MY filters remove", not "how many did I remove first".
      if (!passExchange) exchangeExcluded++;
      if (
        isDuplicateShareClass ||
        !passCountry ||
        !passExchange ||
        !passType ||
        !passGics
      )
        classificationGroupExcluded++;

      // Every badge under Liquidity & Investability, from the five individual
      // sliders up to the card itself. Each level is a UNION of what sits
      // below it, and each is independent of its siblings — a name failing
      // both price and market cap counts on both sliders, and once on the card.
      if (!passPrice) priceExcluded++;
      if (!passAdv) advExcluded++;
      if (!passFloat) floatExcluded++;
      if (!passMcap) marketCapExcluded++;
      if (!passHistory) historyExcluded++;
      if (!passPrice || !passAdv || !passFloat) liquidityExcluded++;
      if (!passMcap) sizeExcluded++;
      if (!passLiquidity) liquidityGroupExcluded++;

      // "Available" sets: passes all user filters EXCEPT the one being skipped
      const baseAll =
        passCountry && passExchange && passType && passGics && passLiquidity;
      // Countries and exchanges are ONE filter in the UI ("Regions, Countries,
      // & Exchanges"), so the availability of a country skips both — otherwise
      // excluding a venue would grey out the country that hosts it.
      const baseNoCountry = passType && passGics && passLiquidity;
      const baseNoType =
        passCountry && passExchange && passGics && passLiquidity;
      const baseNoGics =
        passCountry && passExchange && passType && passLiquidity;

      // Scope/size available: passes all user filters (scope/size are pre-applied in Layer 2)
      if (baseAll) {
        availableScopes.add(s.scope);
        if (s.size !== null) availableSizes.add(s.size);
      }

      if (baseNoCountry) availableCountryCodes.add(s.country);
      if (baseNoType) availableTypes.add(s.securityType);
      if (baseNoGics) {
        availableSectors.add(s.sectorCode);
        availableIndustries.add(s.industryCode);
        availableSubIndustries.add(s.subIndustryCode);
      }
    }

    return {
      filtered,
      availableSizes,
      availableTypes,
      availableCountryCodes,
      availableScopes,
      availableSectors,
      availableIndustries,
      availableSubIndustries,
      // A LIVE ESTIMATE, not the backtest's answer. Every count here is
      // Layer 2 minus Layer 3 over TODAY's constituents, while the backtest
      // runs point-in-time over the whole configured range. Excluding
      // something the universe holds nothing for today reads 0 — a retired
      // industry, a GECS 2026 code, Russia — and still removes securities
      // over the part of the range where they were populated. See
      // STARTING_UNIVERSE.md, "N excluded is a live estimate".
      filterExcludedCounts: {
        priceExcluded,
        advExcluded,
        floatExcluded,
        marketCapExcluded,
        historyExcluded,
        liquidityExcluded,
        sizeExcluded,
        exchangeExcluded,
        classificationGroupExcluded,
        liquidityGroupExcluded,
        primaryShareClassExcluded,
        typeExcluded,
        gicsExcluded,
        countryExcluded,
        geographicExcluded,
      },
    };
  }, [
    scopedSecurities,
    deferredLookups,
    deferredExcludeType,
    liquidity,
    size,
    history,
    primaryShareClassOnly,
  ]);

  // Histogram distributions from Layer 2 (scoped population)
  const distributions = useMemo(() => {
    const BIN_COUNT = 40;

    function buildDistribution(
      values: Array<number>,
      binCount: number,
    ): { bins: Array<number>; min: number; max: number; p90?: number } {
      if (values.length === 0) {
        return { bins: [], min: 0, max: 0 };
      }
      const lo = Math.min(...values);
      const hi = Math.max(...values);
      // 90th percentile (nearest-rank) — the default cap the "+ Max" button
      // seeds so one click drops the top ~10% of extreme-high values.
      const sortedVals = [...values].sort((a, b) => a - b);
      const p90 =
        sortedVals[
          Math.min(
            sortedVals.length - 1,
            Math.max(0, Math.ceil(0.9 * sortedVals.length) - 1),
          )
        ];
      if (lo === hi) {
        return { bins: [values.length], min: lo, max: hi + 1, p90 };
      }
      const bins = new Array<number>(binCount).fill(0);
      const binWidth = (hi - lo) / binCount;
      for (const v of values) {
        const idx = Math.min(Math.floor((v - lo) / binWidth), binCount - 1);
        bins[idx]++;
      }
      return { bins, min: lo, max: hi, p90 };
    }

    function buildLogDistribution(
      values: Array<number>,
      binCount: number,
    ): { bins: Array<number>; min: number; max: number } {
      const positive = values.filter((v) => v > 0);
      if (positive.length === 0) return { bins: [], min: 0, max: 0 };
      const lo = Math.min(...positive);
      const hi = Math.max(...positive);
      if (lo === hi) return { bins: [positive.length], min: lo, max: hi * 2 };
      const logLo = Math.log10(lo);
      const logHi = Math.log10(hi);
      const bins = new Array<number>(binCount).fill(0);
      const logWidth = (logHi - logLo) / binCount;
      for (const v of positive) {
        const idx = Math.min(
          Math.floor((Math.log10(v) - logLo) / logWidth),
          binCount - 1,
        );
        bins[idx]++;
      }
      return { bins, min: lo, max: hi };
    }

    // Visual-only trim of the PRICE chart. A handful of very high-priced names
    // stretch the axis so far that the entire body of the distribution
    // collapses into the leftmost bins. Only the chart is trimmed: every
    // security stays in the population, and `fullMax` carries the true ceiling
    // through so the filter's own default range still reaches it.
    //
    // Price only, for now, and a flat top-1% because that is what was asked
    // for. It is not the statistically ideal rule — these distributions are
    // log-normal, so log-space Tukey fences would adapt to each universe and
    // cut ~0.1% instead of a fixed 1% — but a flat percentile is predictable
    // and easy to state. Revisit before extending this to ADV / market cap.
    const PRICE_CHART_QUANTILE = 0.99;

    // History chart shows only up to 5 years. The filter takes a MINIMUM only
    // (maxAvailable=false on Listing History), so names listed longer than ~5y
    // add nothing to read and just squash the 0-5y body of the distribution.
    // Frontend display trim only — the min filter still uses each name's real
    // listingHistory.
    const HISTORY_CHART_MAX_DAYS = 5 * 365;

    const prices: Array<number> = [];
    const advValues: Array<number> = [];
    const floatValues: Array<number> = [];
    const mcapValues: Array<number> = [];
    const historyValues: Array<number> = [];
    for (const s of scopedSecurities) {
      if (s.price != null) prices.push(s.price);
      // Unmeasured securities are omitted from the histogram, like `price`.
      // Pushing them as 0 piled 73.5% of the universe into the leftmost bin and
      // made the distribution unreadable for the names that DO have data.
      if (s.averageDollarVolume != null) advValues.push(s.averageDollarVolume);
      floatValues.push(s.floatFactor * 100);
      mcapValues.push(s.marketCap);
      if (s.listingHistory != null) historyValues.push(s.listingHistory);
    }

    const sortedPrices = [...prices].sort((a, b) => a - b);
    const priceFullMax = sortedPrices.length
      ? sortedPrices[sortedPrices.length - 1]
      : 0;
    // Nearest-rank percentile: ceil(q·n) − 1. Flooring instead lands ON the
    // first excluded name for a round n (100 names → index 99 → the extreme
    // itself), so the trim kept exactly what it was meant to drop.
    const priceChartMax = sortedPrices.length
      ? sortedPrices[
          Math.min(
            sortedPrices.length - 1,
            Math.max(
              0,
              Math.ceil(PRICE_CHART_QUANTILE * sortedPrices.length) - 1,
            ),
          )
        ]
      : 0;
    const chartPrices = prices.filter((p) => p <= priceChartMax);
    const chartHistory = historyValues.filter(
      (h) => h <= HISTORY_CHART_MAX_DAYS,
    );

    return {
      linear: {
        price: {
          ...buildDistribution(chartPrices, BIN_COUNT),
          fullMax: priceFullMax,
        },
        adv: buildDistribution(advValues, BIN_COUNT),
        float: buildDistribution(floatValues, BIN_COUNT),
        mcap: buildDistribution(mcapValues, BIN_COUNT),
        history: buildDistribution(chartHistory, BIN_COUNT),
      },
      log: {
        price: {
          ...buildLogDistribution(chartPrices, BIN_COUNT),
          fullMax: priceFullMax,
        },
        adv: buildLogDistribution(advValues, BIN_COUNT),
        float: buildLogDistribution(floatValues, BIN_COUNT),
        mcap: buildLogDistribution(mcapValues, BIN_COUNT),
        history: buildLogDistribution(chartHistory, BIN_COUNT),
      },
    };
  }, [scopedSecurities]);

  return {
    // Layer 3 — filtered by user exclusions
    securities: filterResults.filtered,
    totalCount: scopedSecurities.length,
    // Layer 1 raw count — names belonging to the selected universe before
    // ANY wizard-side filter (scope, size, type, GICS, liquidity). Lets the
    // preview render "filtered / universeSize" so the user sees the gap
    // without having to open the exclusion drilldown. O(1) — reuses the
    // useMemo result above, doesn't cost another pass.
    universeSize: universeSecurities.length,
    // Layer-1 total market cap — the denominator the coverage stat scales by.
    universeMarketCap,
    availableSizeSegments: filterResults.availableSizes,
    availableSecurityTypes: filterResults.availableTypes,
    availableSectorCodes: filterResults.availableSectors,
    availableIndustryCodes: filterResults.availableIndustries,
    availableSubIndustryCodes: filterResults.availableSubIndustries,
    availableCountries: filterResults.availableCountryCodes,
    availableGeographicScopes: filterResults.availableScopes,
    // Layer 2 — scoped population (universe + geographic scope + size +
    // cyclicality, BEFORE the user's type/GICS/country/liquidity exclusions).
    // Structural rules like Primary-Share-Class are measured here: the engine
    // applies them at prefetch step 2, ahead of the eligibility filters, so
    // their impact must not move when the user excludes a sector or a country.
    scopedSecurities,
    // Layer 2 — scoped population
    dataRanges,
    distributions,
    filterExcludedCounts: {
      ...filterResults.filterExcludedCounts,
      ...selectorExcludedCounts,
    },
    // Layer 1 — universe scope (what exists before any filters)
    universeSecurityTypes: universeScope.securityTypes,
    universeSectorCodes: universeScope.sectorCodes,
    universeIndustryCodes: universeScope.industryCodes,
    universeSubIndustryCodes: universeScope.subIndustryCodes,
    universeCountries: universeScope.countries,
    universeMics: universeScope.mics,
    isLoading,
    error,
    // Availability of the CH-MEASURED population. Distinguishes "still
    // fetching" from "settled with nothing" — `securitiesUnavailable` alone
    // cannot, so consumers rendered a confident 0 during BOTH.
    securitiesStatus: toDataStatus(isLoading, rawSecurities.length > 0),
    // True when ClickHouse returned no securities at all (e.g. an instance
    // without Morningstar data). Consumers use this to bypass universe-scoping
    // of the classification/country catalogs and fall back to the full static
    // lists, so the eligibility config can still be assembled offline. Kept
    // alongside the status because for THAT purpose loading and unavailable are
    // correctly treated the same: disable nothing, scope to nothing.
    securitiesUnavailable: rawSecurities.length === 0,
  };
}
